import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { MdGridView, MdCheckBox, MdDashboard, MdTrackChanges, MdAccessTime, MdSearch, MdNotifications, MdPlayCircleOutline, MdClose, MdAssignment, MdCheckCircle, MdHourglassEmpty, MdLocalFireDepartment } from 'react-icons/md';

export default function HeroSection() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const dashboardY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Product+Sans:wght@400;500;700&display=swap');
          .google-font {
            font-family: 'Product Sans', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          }
        `}
      </style>
      
      {/* ===== NAVBAR - Clean White ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1a73e8] rounded-lg flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none" className="text-white">
                <path d="M12 8L8 12L12 16M20 8L24 12L20 16M16 4L14 20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="text-xl font-bold text-[#202124] tracking-tight google-font">
              Snappy
            </span>
          </div>

          {/* Center Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {['Overview', 'Features', 'Teams', 'Pricing', 'Resources'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-[#5f6368] hover:text-[#202124] transition-colors text-[15px] font-medium google-font"
              >
                {item}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="hidden sm:block text-[#1a73e8] font-medium text-[15px] hover:bg-blue-50 px-4 py-2 rounded-full transition-colors google-font"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="px-6 py-2.5 bg-[#1a73e8] text-white rounded-full text-[15px] font-medium hover:bg-[#1557b0] transition-colors shadow-sm google-font"
            >
              Get started
            </Link>
          </div>
        </div>
      </nav>

      {/* ===== HERO SECTION ===== */}
      <section ref={ref} className="relative pt-32 pb-20 overflow-hidden bg-white">
        {/* Soft Google-esque background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[50%] bg-[#e8f0fe] rounded-full blur-[100px] opacity-70"></div>
          <div className="absolute top-[20%] right-[-10%] w-[35%] h-[60%] bg-[#e6f4ea] rounded-full blur-[120px] opacity-60"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-[50%] h-[40%] bg-[#fce8e6] rounded-full blur-[100px] opacity-50"></div>
        </div>

        <div className="max-w-[1200px] mx-auto relative z-10 px-6 pt-16 lg:pt-24 text-center">
          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[48px] md:text-[64px] lg:text-[80px] font-bold text-[#202124] leading-[1.05] tracking-[-0.02em] mb-6 google-font max-w-4xl mx-auto"
          >
            Organize your life's work, seamlessly.
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[18px] md:text-[22px] text-[#5f6368] max-w-3xl mx-auto mb-10 leading-[1.5] google-font font-normal"
          >
            Snappy brings your teams, tasks, and tools together in one unified workspace. Designed for clarity, speed, and peace of mind.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-24"
          >
            <Link
              to="/register"
              className="px-8 py-4 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors shadow-[0_4px_14px_rgba(26,115,232,0.3)] text-[16px] active:scale-95 flex items-center justify-center min-w-[200px]"
            >
              Try Snappy for free
            </Link>
            <button
              onClick={() => setIsVideoOpen(true)}
              className="px-8 py-4 bg-white text-[#202124] rounded-full font-medium hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm text-[16px] active:scale-95 flex items-center justify-center gap-2 min-w-[200px]"
            >
              <MdPlayCircleOutline size={22} className="text-[#1a73e8]" /> Watch Demo
            </button>
          </motion.div>

          {/* Dashboard Mockup - with massive Google-style border radius */}
          <motion.div
            style={{ y: dashboardY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative z-20 max-w-[1100px] mx-auto px-4"
          >
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
            onClick={() => setIsVideoOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full max-w-5xl aspect-video bg-black rounded-[2rem] overflow-hidden shadow-2xl flex items-center justify-center group"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsVideoOpen(false)}
                className="absolute top-4 right-4 z-10 w-12 h-12 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-colors"
              >
                <MdClose size={24} />
              </button>
              
              {/* Dummy Video Player UI */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a73e8]/20 to-purple-600/20 flex flex-col items-center justify-center">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center cursor-pointer group-hover:scale-110 transition-transform shadow-[0_0_40px_rgba(26,115,232,0.4)]">
                  <MdPlayCircleOutline size={40} className="text-white translate-x-0.5" />
                </div>
                <p className="text-white mt-6 font-medium text-lg tracking-wide opacity-90">Snappy Product Overview</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ===== DASHBOARD MOCKUP - Google Health Style ===== */
function DashboardMockup() {
  const stats = [
    { icon: <MdAssignment size={24} />, value: '256k', change: '+2.5%', label: 'Incoming Tasks', iconBg: '#e8f0fe', iconColor: '#1a73e8' },
    { icon: <MdCheckCircle size={24} />, value: '136k', change: '+14.8%', label: 'Completed Tasks', iconBg: '#e6f4ea', iconColor: '#1e8e3e' },
    { icon: <MdHourglassEmpty size={24} />, value: '120k', change: '+5.1%', label: 'In Progress', iconBg: '#fef7e0', iconColor: '#f9ab00' },
    { icon: <MdLocalFireDepartment size={24} />, value: '93', change: '+23.5%', label: 'Total Overdue', iconBg: '#fce8e6', iconColor: '#d93025' },
  ];

  const sideNavItems = [
    { icon: <MdGridView size={20} />, label: 'Overview', active: true },
    { icon: <MdCheckBox size={20} />, label: 'My Tasks', active: false },
    { icon: <MdDashboard size={20} />, label: 'Projects', active: false },
    { icon: <MdTrackChanges size={20} />, label: 'Focus', active: false },
    { icon: <MdAccessTime size={20} />, label: 'History', active: false },
  ];

  return (
    <div className="bg-white rounded-[2.5rem] overflow-hidden border border-gray-100 google-font" style={{ boxShadow: '0 24px 64px -12px rgba(0,0,0,0.08), 0 12px 24px -8px rgba(0,0,0,0.04)' }}>
      {/* Top App Bar (Google Style) */}
      <div className="h-[72px] border-b border-gray-100 flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center text-gray-500 cursor-pointer transition-colors">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
          <span className="text-[22px] font-normal text-[#5f6368] tracking-tight">Snappy <span className="font-medium text-[#202124]">Workspace</span></span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 bg-[#f1f3f4] rounded-full px-4 py-2.5 w-[400px] border border-transparent focus-within:bg-white focus-within:border-gray-300 focus-within:shadow-sm transition-all">
            <MdSearch className="text-[#5f6368]" size={20} />
            <input type="text" placeholder="Search across your workspace" className="bg-transparent border-none outline-none text-[15px] text-[#202124] placeholder-[#5f6368] w-full" />
          </div>
          <div className="w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center text-[#5f6368] cursor-pointer transition-colors">
            <MdNotifications size={22} />
          </div>
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full border-2 border-white cursor-pointer shadow-sm"></div>
        </div>
      </div>

      <div className="flex min-h-[480px]">
        {/* LEFT SIDEBAR */}
        <div className="w-[280px] bg-white border-r border-gray-100 py-4 px-3 hidden md:flex flex-col">
          {/* Compose Button */}
          <button className="flex items-center gap-3 bg-[#c2e7ff] text-[#001d35] px-5 py-4 rounded-[1.25rem] font-medium text-[15px] hover:shadow-md transition-all mb-4 w-[160px]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            Create
          </button>

          {/* Nav Items */}
          <div className="space-y-1">
            {sideNavItems.map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-4 px-5 py-3.5 rounded-full text-[14px] font-medium transition-all cursor-pointer ${
                  item.active
                    ? 'bg-[#e8f0fe] text-[#1a73e8]'
                    : 'text-[#3c4043] hover:bg-[#f1f3f4]'
                }`}
              >
                <div className={`${item.active ? 'text-[#1a73e8]' : 'text-[#5f6368]'}`}>
                  {item.icon}
                </div>
                {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 p-6 md:p-8 bg-white">
          <h2 className="text-[28px] font-normal text-[#202124] mb-8">Good afternoon, Sayan</h2>

          {/* Stat Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white rounded-[1.5rem] p-5 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl" style={{ backgroundColor: stat.iconBg, color: stat.iconColor }}>
                    {stat.icon}
                  </div>
                  <span className="text-[12px] text-[#1e8e3e] font-medium bg-[#e6f4ea] px-2 py-1 rounded-full">{stat.change}</span>
                </div>
                <div className="text-[32px] font-normal text-[#202124] leading-tight mb-1">{stat.value}</div>
                <div className="text-[14px] font-medium text-[#5f6368]">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Bottom Content Area */}
          <div className="bg-[#f8f9fa] rounded-[1.5rem] p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[18px] font-medium text-[#202124]">Activity overview</h3>
              <div className="flex items-center gap-2">
                <button className="text-[14px] font-medium text-[#1a73e8] hover:bg-blue-50 px-4 py-2 rounded-full transition-colors">
                  View full report
                </button>
              </div>
            </div>
            
            <div className="flex items-end gap-6 h-32 opacity-80">
              {/* Simulated Chart replacing the old one */}
              <div className="flex-1 flex items-end justify-between h-full px-2">
                {[40, 60, 30, 80, 50, 90, 70].map((height, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 w-8">
                    <div 
                      className="w-full rounded-t-lg bg-[#e8f0fe] transition-all duration-500 hover:bg-[#1a73e8]" 
                      style={{ height: `${height}%` }}
                    ></div>
                    <span className="text-[11px] text-[#5f6368] font-medium">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
